import React, { useEffect, useState } from "react";
import { fetchPopulation, fetchReportResources } from "../../services/httpClient";
import html2canvas from "html2canvas";
import { ButtonGroup, Col, Dropdown, Row, Spinner } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import ReportTimeInterval from "./ui/ReportTimeInterval";
import { faDownload } from "@fortawesome/free-solid-svg-icons";
import { ResourcesMetric } from "./charts-metrics/ResourcesMetric";
import { getCurrentDateTime } from "../../helpers/date";
import { signal } from "@preact/signals-react";
import { CSVLink } from "react-csv";
import { ResourceAssignmentPieChart } from "./charts-metrics/ResourceAssignmentPieChart";
import { ResourcesAssignedTable } from "./charts-metrics/ResourcesAssignedTable";
import { activeKey } from "./Reports";

export const showResources = signal(false);

const ResourcesTab = ({ buildPDF, canvas, setCanvas, iPad }: any) => {
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [startDate, setStartDate] = useState<string>();
  const [endDate, setEndDate] = useState<string>();
  const [dateInterval, setDateInterval] = useState<number>(365);
  const [data, setData] = useState<any>();
  const [reportRunning, setReportRunning] = useState<boolean>(false);

  const defaultCSVHeaders = [
    { label: "Resource ID", key: "resource_id", hide: true },
    { label: "Resource", key: "resource_name" },
    { label: "Resource Type", key: "resource_type_key", hide: true },
    { label: "Resource Category", key: "resource_category" },
    { label: "Case ID", key: "case_id" },
    { label: "Case", key: "case_name" },
    { label: "Name", key: "assignee_name" },
    { label: "Assigned Date", key: "assigned_at" },
    { label: "Returned Date", key: "returned_at" },
  ];

  useEffect(() => {
    if (activeKey.value === "resources") {
      const start = new Date();
      start.setDate(start.getDate() - dateInterval);
      const startString = start.toISOString().slice(0, 10);
      const endString = new Date().toISOString().slice(0, 10);
      setStartDate(startString);
      setEndDate(endString);
      getData(startString, endString);
    }
  }, [activeKey.value]);

  useEffect(() => {
    if (reportRunning) {
      buildPDF();
      setReportRunning(false);
    }
  }, [canvas]);

  const getData = async (start: string, end: string) => {
    const [resources, population] = await Promise.all([getResourceAssignments(start, end), getPopulation(start, end)]);

    setData({
      resources,
      population,
    });
    setIsLoading(false);
  };

  const getResourceAssignments = async (start: string, end: string) => {
    return fetchReportResources(start, end)
      .then((res: any) => {
        const resources = res[0].data;
        return resources;
      })
      .catch((err: any) => {
        console.log(err);
        return null;
      });
  };

  const getPopulation = async (start: string, end: string) => {
    return fetchPopulation(start, end)
      .then((res: any) => {
        const population = res[0].data;
        return population;
      })
      .catch((err: any) => {
        console.log(err);
        return null;
      });
  };

  const onClickDownloadReport = async () => {
    setReportRunning(true);
    const reportSection = document.getElementById("resources-section");
    const reportTitle = document.getElementById("resources-title");
    const refreshButton = document.getElementById("resources-refresh-button");
    const downloadConsumablesButton = document.getElementById("download-consumables-button");
    const downloadNonConsumablesButton = document.getElementById("download-nonconsumables-button");

    if (reportSection) {
      if (reportTitle) {
        reportTitle.style.visibility = "hidden";
        reportTitle.style.display = "none";
      }
      if (refreshButton) refreshButton.style.visibility = "hidden";
      if (downloadConsumablesButton) downloadConsumablesButton.style.visibility = "hidden";
      if (downloadNonConsumablesButton) downloadNonConsumablesButton.style.visibility = "hidden";

      const images = reportSection.getElementsByTagName("img");
      for (let i = 0; i < images.length; i++) {
        images[i].crossOrigin = "anonymous";
      }

      const canvas = await html2canvas(reportSection, { useCORS: true });
      setCanvas(canvas);

      if (reportTitle) {
        reportTitle.style.visibility = "visible";
        reportTitle.style.display = "flex";
      }
      if (refreshButton) refreshButton.style.visibility = "visible";
      if (downloadConsumablesButton) downloadConsumablesButton.style.visibility = "visible";
      if (downloadNonConsumablesButton) downloadNonConsumablesButton.style.visibility = "visible";
    } else {
      console.error("Report section not found");
      setReportRunning(false);
    }
  };

  return (
    <div id="resources-section">
      {isLoading ? (
        <div className="text-center mt-5">
          <Spinner animation="border" style={{ color: "#F6893D" }} />
        </div>
      ) : (
        <>
          <Row id="resources-title" className="mb-2">
            <Col className="col-8">
              <h4 className="hero-heading app-header-text">Resources</h4>
            </Col>
            <Col className="text-end col-4">
              {!iPad && (
                <Dropdown as={ButtonGroup}>
                  <Dropdown.Toggle id="dropdown-custom-1" className="app-primary-color button">
                    <FontAwesomeIcon icon={faDownload} className="me-2" />
                    Downloads
                  </Dropdown.Toggle>
                  <Dropdown.Menu className="super-colors">
                    <Dropdown.Item eventKey="1" onClick={() => onClickDownloadReport()}>
                      Report
                    </Dropdown.Item>
                    <Dropdown.Item
                      eventKey="2"
                      as={CSVLink}
                      data={data?.resources ? data?.resources : [{}]}
                      headers={defaultCSVHeaders}
                      filename={`resources_${getCurrentDateTime()}.csv`}
                      target="_blank"
                    >
                      Data Export
                    </Dropdown.Item>
                  </Dropdown.Menu>
                </Dropdown>
              )}
            </Col>
          </Row>
          <ReportTimeInterval
            startDate={startDate}
            setStartDate={setStartDate}
            endDate={endDate}
            setEndDate={setEndDate}
            dateInterval={dateInterval}
            setDateInterval={setDateInterval}
            intervalOne={30}
            intervalOneText={"30 Days"}
            intervalTwo={90}
            intervalTwoText={"90 Days"}
            intervalThree={180}
            intervalThreeText={"180 Days"}
            intervalFour={365}
            intervalFourText={"Last Year"}
            onClickRefresh={getData}
            hideBtnId="resources-refresh-button"
          />
          <Row className="mt-3 mb-5">
            <Col xs={3} className="p-2">
              <ResourcesMetric data={data} metricType="people" />
            </Col>
            <Col xs={3} className="p-2">
              <ResourcesMetric data={data} metricType="resources" resourceType="consumable" />
            </Col>
            <Col xs={3} className="p-2">
              <ResourcesMetric data={data} metricType="resources" resourceType="nonconsumable" />
            </Col>
            <Col xs={3} className="p-2">
              <ResourcesMetric data={data} metricType="usage" />
            </Col>
          </Row>
          <ResourceAssignmentPieChart data={data} />
          <ResourcesAssignedTable data={data} />
        </>
      )}
    </div>
  );
};

export default ResourcesTab;
